import React, { useState, useEffect, useRef } from "react";
import classnames from "classnames";
import Slider from "react-slick";
import _omitBy from "lodash/omitBy";
import _isUndefined from "lodash/isUndefined";
import { gettext } from "web/script/modules/django-i18n";
import analytics from "web/script/analytics/analytics";
import userProfiler from "web/script/modules/userprofiler";
import { useRothko } from "web/react/hooks/use-rothko/use-rothko";
import { useIntersectionObserver } from "web/react/hooks/use-intersection-observer/use-intersection-observer";
import { RecentlyViewedSerializer } from "web/types/serializers";
import LoadingSpinner from "web/react/components/loading-spinner/loading-spinner";
import ProductCard from "web/react/components/product-card/react";
import SecondaryButton from "web/react/components/buttons/secondary-button/secondary-button";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import styles from "./recently-viewed.css";

const RECENTLY_VIEWED_ENDPOINT = "modules/recently_viewed";

function getRecentlySeenProductIds(number: number, currentProductId: number | null): number[] {
    // If we're on a product page we're going to exclude
    // the current product from the recently viewed module.
    if (currentProductId) {
        number++;
    }

    const seenProducts = userProfiler
        .getSeenProducts()
        .slice(-number)
        .reverse();
    const productIds = seenProducts.reduce((memo, product) => {
        if (product.productId !== currentProductId) {
            memo.push(product.productId);
        }

        return memo;
    }, []);

    return productIds;
}

function makeApiParams(recentlyViewedProducts, inOverlay): any {
    return _omitBy(
        {
            product_id: recentlyViewedProducts,
            in_overlay: inOverlay,
        },
        _isUndefined
    );
}

interface SliderArrowProps {
    className?: string;
    onClick?: () => void;
}

export function PrevArrow({ onClick, className }: SliderArrowProps): React.ReactElement {
    function handlePrev(): void {
        analytics.event("product-cards", "paginated", "previous_button");
        onClick?.();
    }

    return (
        <SecondaryButton
            className={classnames(className, styles.navButton, styles.prevNavButton)}
            small
            onClick={handlePrev}
        >
            <SVGIcon name="chevron" />
            {"Previous"}
        </SecondaryButton>
    );
}

export function NextArrow({ onClick, className }: SliderArrowProps): React.ReactElement {
    function handleNext(): void {
        analytics.event("product-cards", "paginated", "next_button");
        onClick?.();
    }

    return (
        <SecondaryButton
            className={classnames(className, styles.navButton, styles.nextNavButton)}
            small
            onClick={handleNext}
        >
            {"Next"} <SVGIcon name="chevron" />
        </SecondaryButton>
    );
}

function RecentlyViewed({
    title,
    current_product_id: currentProductId,
    in_overlay: inOverlay,
}: RecentlyViewedSerializer): React.ReactElement | null {
    const [recentlySeenProductIds, setRecentlySeenProductIds] = useState<number[] | null>(null);
    const params = makeApiParams(recentlySeenProductIds, inOverlay);

    const { data, isFetching, run } = useRothko<RecentlyViewedSerializer>(
        RECENTLY_VIEWED_ENDPOINT,
        params,
        {
            enabled: false,
        }
    );
    const recentlyViewedRef = useRef<HTMLDivElement | null>(null);
    const sliderRef = useRef<any>(null);
    const [isVisible] = useIntersectionObserver(
        {
            rootMargin: "0px 0px 250px 0px",
            once: true,
        },
        recentlyViewedRef
    );

    useEffect(() => {
        if (isVisible) {
            let recentlySeen = getRecentlySeenProductIds(10, currentProductId);
            setRecentlySeenProductIds(recentlySeen);

            if (recentlySeen && recentlySeen.length) {
                run();
            }
        }
    }, [isVisible, currentProductId, run]);

    // Don't render if we didn't store recently seen products
    if (recentlySeenProductIds !== null && !recentlySeenProductIds.length) {
        return null;
    }

    const slickSettings = {
        arrows: true,
        draggable: false,
        infinite: false,
        slidesToShow: 5,
        slidesToScroll: 5,
        swipe: false,
        touchMove: false,
        prevArrow: <PrevArrow />,
        nextArrow: <NextArrow />,
        responsive: [
            {
                breakpoint: 992,
                settings: {
                    slidesToShow: 3.2,
                    slidesToScroll: 2,
                    swipe: true,
                    touchMove: true,
                    draggable: true,
                    arrows: false,
                },
            },
            {
                breakpoint: 768,
                settings: {
                    slidesToShow: 2.5,
                    slidesToScroll: 1,
                    swipe: true,
                    touchMove: true,
                    draggable: true,
                    arrows: false,
                },
            },
        ],
    };

    return (
        <div ref={recentlyViewedRef} className={styles.wrapper}>
            <div className={styles.container}>
                <h4 className={styles.title}>
                    {isFetching ? gettext("product.recently_viewed.title_loading") : title}
                </h4>

                {isFetching ? (
                    <div className={styles.loader}>
                        <LoadingSpinner type="highlight" />
                    </div>
                ) : (
                    <Slider ref={sliderRef} className={styles.products} {...slickSettings}>
                        {data?.products?.map((product) => (
                            <div key={product.uid} className={styles.productCard}>
                                <ProductCard product={product}>
                                    <ProductCard.Image width={200} height={250} />

                                    <ProductCard.SaveForLaterButton />

                                    <ProductCard.Details />
                                </ProductCard>
                            </div>
                        ))}
                    </Slider>
                )}
            </div>
        </div>
    );
}

export default RecentlyViewed;
